<template>
  <div class="tableWithPerPageSelection">
    <div class="entete border-entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3">{{ $t('addProduitsAssocies.title')}}</h1>
              <app-breadcrumb :breadcrumb="breadcrumb" />
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <a @click="annuler()"
               class="button">
              {{ $t('general.button.cancel')}}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div class="level pdt-10px mgb-0">
      <div class="level-left has-text-default">
        <span> {{ $t('addProduitsAssocies.periode') }}</span>
        <b-datepicker show-week-number
                      nearby-selectable-month-days
                      icon="calendar-today"
                      class="mgl-5px"
                      v-model="periode.dateDebut"
                      :editable="true">
        </b-datepicker>
        <span class="mgl-5px"> {{ $t('general.au') }} </span>
        <b-datepicker show-week-number
                      class="mgl-5px"
                      nearby-selectable-month-days
                      icon="calendar-today"
                      v-model="periode.dateFin"
                      :editable="true">
        </b-datepicker>
      </div>
      <div class="level-right column is-narrow">
        <div class="column is-pulled-right is-paddingless">
          <button class="button" @click='showSelection = !showSelection'>
            <b-icon icon="cart-outline" size="is-small" type="is-default"></b-icon>
            <span class="is-size-7">
              {{ $t('general.button.selection') }}
            </span>
          </button>
          <div class="info-circle-right background-danger is-pulled-right"
               v-if="selection.length > 0">
            <span class="inside-circle">
              {{ selection.length > 9 ? '9+' : selection.length }}
            </span>
          </div>
        </div>
        <div class="column is-pulled-right is-paddingless">
          <button class="button" @click='showRecherche = !showRecherche'>
            <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
            <span class="is-size-7">
              {{ $t('general.button.maRecherche') }}
            </span>
          </button>
          <div class="info-circle-right background-primary is-pulled-right"
               :class="{'is-invisible': nombreFiltre === 0}">
            <span class="inside-circle">{{ nombreFiltre }}</span>
          </div>
        </div>
      </div>
    </div>
    <v-server-table url=""
                    :columns="columns"
                    :options="options"
                    @loaded="finLoading"
                    @loading="debutLoading"
                    @error="finLoading"
                    class="tableWithChildRow"
                    ref="tableListeProduits">
      <template slot='afterTable'>
        <table-items-in-view :perPage="constantes.RESULTS_PER_PAGE_PLANNING"
                             @input="$refs.tableListeProduits.setLimit($event)"/>
      </template>
      <div slot="addPanier" slot-scope="props" class="has-text-centered">
        <button
          @click="addLiaisonTarif(props.row)"
          class="ripple button"
          :class="getColorAddPanier(props.row.id)">
          <b-icon icon="plus-circle"></b-icon>
        </button>
      </div>
      <div slot="type" slot-scope="props">
        {{ $t(getTypeTrad(props.row)) }}
      </div>
    </v-server-table>
    <transition name="custom-classes-transition"
                enter-active-class="animate__animated animate__slideInRight"
                leave-active-class="animate__animated animate__slideOutRight" :duration="10">
      <div class="componentBrief" v-if="showRecherche || showSelection">
        <selection
          @ajoutProduitAssociesAuProduit="ajoutProduitAssociesAuProduit"
          @toggleSelection="showSelection = !showSelection"
          :reference="produit.reference"
          v-if="showSelection"/>
        <recherche v-if="showRecherche"
                   :filtres-base="filtres"
                   @toggleCriteresRecherche="showRecherche = !showRecherche"
                   @appliquerFiltres="appliquerFiltres"/>
      </div>
    </transition>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import AppBreadcrumb from '@/components/general/breadcrumb';
import cloneDeep from 'lodash/cloneDeep';
import {
  TEXTS, SORT_ICON, NO_PER_PAGE_VALUE, RESULTS_PER_PAGE_PLANNING,
} from '@/configuration/clientTableConf';
import TableItemsInView from '@/components/general/TableItemsInView';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import genererFiltreProduitAssocie
  from '@/services/bodac/produit/produitAssocie/genererFiltreProduitAssocie';
import {
  CATEGORIE_LOCATION,
  CATEGORIE_DIVERS,
  CATEGORIE_REGROUPEMENT_MAIRIE,
} from '@/configuration/catalogue/produitConf';
import childrowInfoDivers from '@/components/bodac/produit/produitAssocie/childrowInfoDivers';
import Selection from '@/components/bodac/produit/produitAssocie/selection';
import formatageProduitAssocie
  from '@/services/bodac/produit/produitAssocie/formatageProduitAssocie';
import messageErreur from '@/services/general/messageErreur';
import { get as getProduitLocation } from '@/api/produit/produitLocation';
import { get as getProduitDivers } from '@/api/produit/produitDivers';
import Recherche from '@/components/bodac/produit/produitAssocie/recherche';
import { SENS_VENTE } from '@/configuration/devisConf';

const Produit = createNamespacedHelpers('produit');
const Societe = createNamespacedHelpers('societe');

export default {
  data() {
    return {
      showRecherche: false,
      showSelection: false,
      filtres: genererFiltreProduitAssocie(),
      constantes: {
        RESULTS_PER_PAGE_PLANNING,
      },
      periode: {
        dateDebut: new Date(),
        dateFin: null,
      },
    };
  },
  components: {
    AppBreadcrumb,
    TableItemsInView,
    Selection,
    Recherche,
  },
  computed: {
    ...Produit.mapGetters({
      produit: 'produit',
      produits: 'produitsAssocies',
      totalProduits: 'totalProduitsAssocies',
      selection: 'selection',
      societeForGetAll: 'societeForGetAll',
    }),
    ...Societe.mapGetters({
      societesInformations: 'societesInformations',
    }),
    nombreFiltre() {
      const nbFiltre = calculNombreCritere(this.filtres, []);
      if (nbFiltre > 9) return '9+';
      return nbFiltre;
    },
    columns() {
      return ['addPanier', 'reference', 'libelle', 'type'];
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: {
          addPanier: '',
          reference: this.$i18n.t('addProduitsAssocies.tab.ref'),
          libelle: this.$i18n.t('addProduitsAssocies.tab.libelle'),
          type: this.$i18n.t('addProduitsAssocies.tab.type'),
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE_PLANNING,
        texts: TEXTS,
        sortable: ['reference', 'libelle'],
        childRow: this.hasLiaisonsTarifs ? childrowInfoDivers : null,
        rowClassCallback: ((row) => {
          if (row.liaisonsTarif.length === 0 && this.hasLiaisonsTarifs) {
            return 'hideChildToggler disableChildRowClick';
          }
          return 'activeChildRowClick';
        }),
        columnsClasses: {
          addPanier: 'colonneSelected-35 is-paddingless',
        },
        requestFunction: ((data) => {
          if (!data.query) data.query = cloneDeep(this.filtres);
          data.query.sens = this.$route.params.sens ? this.$route.params.sens : SENS_VENTE;
          if (data.query.categorie.length === 0) {
            data.query.categorie = [CATEGORIE_DIVERS];
            if (!this.produit.produitLocation
              && !this.produit.produitSalon
              && !this.produit.produitAutre
              && !this.produit.produitPresse
              && !this.produit.produitSponsoring
              && this.produit.supportCommercial.categorieVente
                !== CATEGORIE_REGROUPEMENT_MAIRIE) data.query.categorie.push(CATEGORIE_LOCATION);
          }
          if (!data.query.societeId) data.query.societeId = this.societeForGetAll;
          return this.loadProduits(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.produits,
            count: this.totalProduits,
          };
        }),
      };
    },
    hasLiaisonsTarifs() {
      return ((this.produit.produitLocation === null || !this.produit.produitLocation)
        && this.produits.some((produit) => {
          return produit.liaisonsTarif.length !== 0;
        }));
    },
    breadcrumb() {
      const current = {
        title: this.$i18n.t('addProduitsAssocies.breadcrumb.ajoutProduitAssocie'),
        route: { name: '' },
        isActive: true,
      };
      if (Object.prototype.hasOwnProperty.call(this.$route.params, 'breadcrumb')) {
        const output = Array.from(this.$route.params.breadcrumb);
        output[output.length - 1].isActive = false;
        output.push(current);
        return output;
      }
      return [current];
    },
  },
  methods: {
    ...Produit.mapActions({
      loadProduits: 'loadProduitsLocAndDivers',
      updateSelection: 'updateSelection',
      updateProduit: 'updateProduit',
      addLiaisonTarifInSelection: 'addLiaisonTarifInSelection',
    }),
    appliquerFiltres(newFiltres) {
      this.filtres = newFiltres;
      this.recherche();
    },
    async ajoutProduitAssociesAuProduit() {
      this.produit.liaisonsTarif = this.selection;
      this.updateProduit(this.produit);
      await this.$router.go(-1);
    },
    recherche() {
      this.$refs.tableListeProduits.refresh();
    },
    goFirstPage() {
      this.$refs.tableListeProduits.setPage(1);
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    async addLiaisonTarif(produit) {
      const loadingComponent = this.$buefy.loading.open();
      try {
        let infoProduit = null;
        if (produit.produitLocation) {
          infoProduit = (await getProduitLocation(produit.id)).data.result;
        } else if (produit.produitDivers) {
          infoProduit = (await getProduitDivers(produit.id)).data.result;
        }
        const liaisons = cloneDeep(formatageProduitAssocie(infoProduit, this.periode));
        this.addLiaisonTarifInSelection(liaisons);
        loadingComponent.close();
      } catch (e) {
        messageErreur(this.$i18n);
        loadingComponent.close();
        throw e;
      }
    },
    annuler() {
      this.updateSelection([]);
      this.$router.go(-1);
    },
    beforeRouteLeave(to, from, next) {
      if (to.name.includes('m.bodac.catalogue-')) {
        this.updateProduit(null);
        this.updateSelection([]);
      }
      next();
    },
    getColorAddPanier(idProduit) {
      if (!this.selection) return 'is-primary';
      const isAlreadyAdd = this.selection.some((produit) => {
        return idProduit === produit.produitEnfantId;
      });
      return isAlreadyAdd ? 'is-success' : 'is-primary';
    },
    getTypeTrad(produit) {
      if (produit.produitLocation) return 'catalogue.type.location';
      if (produit.produitDivers && produit.produitDivers.tlpe) return 'catalogue.newProduct.tlpe';
      if (produit.produitDivers && produit.produitDivers.honoraire) {
        return 'catalogue.newProduct.honoraire';
      }
      return 'catalogue.type.divers';
    },
  },
  mounted() {
    if (!this.$route.params.breadcrumb) {
      this.annuler();
      return;
    }
    if (this.societesInformations.length > 1) this.filtres.societeId = this.societeForGetAll;
    const selection = cloneDeep(this.produit.liaisonsTarif.filter((liaison) => {
      return liaison.produitEnfantId;
    }));
    selection.forEach((select) => { select.isAlreadySelect = true; });
    this.updateSelection(selection);
    this.setInformationPage(this.$t('pageGescom.produit.ajoutProduitAssocie'));
  },
};
</script>
